import React, { useState, useEffect } from 'react';
import axios from '../config/api';
import { toast } from 'react-toastify';
import { useAuth } from '../context/AuthContext';
import { usePageContext } from '../context/PageContext';
import Select from 'react-select';
import InfoTooltip from '../components/InfoTooltip';

const EnhancedInvoiceManagement = () => {
  const [invoices, setInvoices] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [calculation, setCalculation] = useState(null);
  const [pagination, setPagination] = useState({ current: 1, pages: 1, total: 0, limit: 10 });
  const [filters, setFilters] = useState({ search: '', status: '', sortBy: 'createdAt', sortOrder: 'desc' });
  const [formData, setFormData] = useState({
    employeeId: '',
    month: '',
    timesheetHours: '',
    invoiceNumber: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedInvoices, setSelectedInvoices] = useState([]);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [loading, setLoading] = useState(true);
  const { hasPermission } = useAuth();
  const { setPageTitle } = usePageContext();

  const fetchInvoices = async () => {
    try {
      setLoading(true);
      const params = {
        page: pagination.current,
        limit: pagination.limit,
        ...filters
      };
      const response = await axios.get('/api/invoice', { params });
      setInvoices(Array.isArray(response.data.invoices) ? response.data.invoices : []);
      if (response.data.pagination) {
        setPagination(response.data.pagination);
      }
    } catch (error) {
      toast.error('Error fetching invoices');
      setInvoices([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchEmployees = async () => {
    try {
      const response = await axios.get('/api/employee?limit=1000&fields=name,clientRate,consultantRate,currentCondition');
      setEmployees(Array.isArray(response.data.employees) ? response.data.employees : Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      toast.error('Error fetching employees');
      setEmployees([]);
    }
  };

  const calculatePreview = async () => {
    if (!formData.employeeId || !formData.timesheetHours || isNaN(formData.timesheetHours)) return;
    
    const employee = employees.find(e => e._id === formData.employeeId);
    if (!employee) return;
    
    try {
      const hours = parseFloat(formData.timesheetHours);
      if (hours <= 0) return;
      
      const response = await axios.post('/api/invoice/calculate', {
        employeeId: formData.employeeId,
        currentHours: hours,
        clientRate: employee.clientRate,
        consultantRate: employee.consultantRate,
        conditionId: employee.currentCondition
      });
      setCalculation(response.data);
    } catch (error) {
      toast.error('Error calculating invoice');
    }
  };

  useEffect(() => {
    setPageTitle('Invoice Management');
    fetchInvoices();
    fetchEmployees();
  }, [setPageTitle]);

  useEffect(() => {
    fetchInvoices();
  }, [pagination.current, filters]);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      calculatePreview();
    }, 500);
    return () => clearTimeout(timeoutId);
  }, [formData.employeeId, formData.timesheetHours]);

  useEffect(() => {
    if (showModal && formData.employeeId) {
      setFormData(prev => ({ ...prev, invoiceNumber: generateInvoiceNumber() }));
    }
  }, [showModal, formData.employeeId]);

  const generateInvoiceNumber = () => {
    const date = new Date();
    const year = date.getFullYear().toString().slice(-2);
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const random = Math.floor(Math.random() * 9999).toString().padStart(4, '0');
    return `INV-${year}${month}-${random}`;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;
    
    const employee = employees.find(e => e._id === formData.employeeId);
    if (!employee) return;
    
    setIsSubmitting(true);
    try {
      await axios.post('/api/invoice', {
        employeeId: formData.employeeId,
        month: formData.month,
        timesheetHours: parseFloat(formData.timesheetHours),
        clientRate: employee.clientRate,
        consultantRate: employee.consultantRate,
        conditionId: employee.currentCondition,
        invoiceNumber: formData.invoiceNumber || undefined
      });
      toast.success('Invoice created successfully');
      setShowModal(false);
      setFormData({ employeeId: '', month: '', timesheetHours: '', invoiceNumber: '' });
      setSelectedInvoices([]);
      setCalculation(null);
      fetchInvoices();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Error creating invoice');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleStatusUpdate = async (invoiceId, status) => {
    try {
      await axios.put(`/api/invoice/${invoiceId}/status`, { status });
      toast.success(`Invoice ${status} successfully`);
      fetchInvoices();
    } catch (error) {
      toast.error('Error updating invoice status');
    }
  };

  const handleDelete = async (invoiceId) => {
    try {
      await axios.delete(`/api/invoice/${invoiceId}`);
      toast.success('Invoice deleted successfully');
      fetchInvoices();
    } catch (error) {
      toast.error('Error deleting invoice');
    }
  };

  const handleBulkDelete = async () => {
    try {
      await Promise.all(selectedInvoices.map(id => axios.delete(`/api/invoice/${id}`)));
      toast.success(`${selectedInvoices.length} invoices deleted successfully`);
      setSelectedInvoices([]);
      setShowDeleteModal(false);
      fetchInvoices();
    } catch (error) {
      toast.error('Error deleting invoices');
    }
  };

  const employeeOptions = Array.isArray(employees) ? employees.map(e => ({ 
    value: e._id, 
    label: `${e.name} - $${e.clientRate}/hr` 
  })) : [];

  const getStatusBadgeClass = (status) => {
    switch (status) {
      case 'Draft': return 'bg-secondary';
      case 'Sent': return 'bg-warning';
      case 'Paid': return 'bg-success';
      default: return 'bg-secondary';
    }
  };

  if (!hasPermission('invoice', 'read')) {
    return <div className="alert alert-danger">Access denied</div>;
  }

  return (
    <div className="container-fluid">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div className="d-flex align-items-center gap-3">
          {selectedInvoices.length > 0 && (
            <div className="d-flex align-items-center gap-2">
              <span className="badge bg-primary">{selectedInvoices.length} selected</span>
              <button className="btn btn-sm btn-danger" onClick={() => setShowDeleteModal(true)}>
                <i className="fas fa-trash me-1"></i>Delete Selected
              </button>
            </div>
          )}
          <input
            type="text"
            className="form-control"
            placeholder="Search invoices..."
            value={filters.search}
            onChange={(e) => setFilters({...filters, search: e.target.value})}
            style={{ width: '250px' }}
          />
          <select
            className="form-select"
            value={filters.status}
            onChange={(e) => setFilters({...filters, status: e.target.value})}
            style={{ width: '150px' }}
          >
            <option value="">All Status</option>
            <option value="Draft">Draft</option>
            <option value="Sent">Sent</option>
            <option value="Paid">Paid</option>
          </select>
          <select
            className="form-select"
            value={`${filters.sortBy}-${filters.sortOrder}`}
            onChange={(e) => {
              const [sortBy, sortOrder] = e.target.value.split('-');
              setFilters({...filters, sortBy, sortOrder});
            }}
            style={{ width: '180px' }}
          >
            <option value="createdAt-desc">Newest First</option>
            <option value="createdAt-asc">Oldest First</option>
            <option value="invoiceAmount-desc">Amount High-Low</option>
            <option value="invoiceAmount-asc">Amount Low-High</option>
          </select>
        </div>
        {hasPermission('invoice', 'create') && (
          <button className="btn btn-primary" onClick={() => setShowModal(true)}>
            <i className="fas fa-plus me-2"></i>Create Invoice
          </button>
        )}
      </div>

      {loading ? (
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="mt-2 text-muted">Loading invoices...</p>
        </div>
      ) : invoices.length === 0 ? (
        <div className="text-center py-5">
          <div style={{ fontSize: '4rem', opacity: 0.3 }}>📄</div>
          <h5 className="text-muted mt-3">No invoices found</h5>
          <p className="text-muted">Create your first invoice to get started with billing.</p>
          {hasPermission('invoice', 'create') && (
            <button className="btn btn-primary mt-3" onClick={() => setShowModal(true)}>
              <i className="fas fa-plus me-2"></i>Create First Invoice
            </button>
          )}
        </div>
      ) : (
        <div className="table-responsive">
          <table className="table table-striped">
          <thead>
            <tr>
              <th>
                <input
                  type="checkbox"
                  onChange={(e) => {
                    if (e.target.checked) {
                      setSelectedInvoices(invoices.map(inv => inv._id));
                    } else {
                      setSelectedInvoices([]);
                    }
                  }}
                  checked={selectedInvoices.length === invoices.length && invoices.length > 0}
                />
              </th>
              <th>Invoice #</th>
              <th>Employee</th>
              <th>Month</th>
              <th>Hours</th>
              <th>Invoice Amount</th>
              <th>Caliber Revenue</th>
              <th>Consultant Pay</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {Array.isArray(invoices) ? invoices.map(invoice => (
              <tr key={invoice._id}>
                <td>
                  <input
                    type="checkbox"
                    checked={selectedInvoices.includes(invoice._id)}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setSelectedInvoices([...selectedInvoices, invoice._id]);
                      } else {
                        setSelectedInvoices(selectedInvoices.filter(id => id !== invoice._id));
                      }
                    }}
                  />
                </td>
                <td>{invoice.invoiceNumber}</td>
                <td>{invoice.employee?.name}</td>
                <td>{new Date(invoice.month).toLocaleDateString()}</td>
                <td>{invoice.timesheetHours}</td>
                <td>${invoice.invoiceAmount.toFixed(2)}</td>
                <td>${invoice.caliberRevenue.toFixed(2)}</td>
                <td>${invoice.consultantPay.toFixed(2)}</td>
                <td>
                  <span className={`badge ${getStatusBadgeClass(invoice.status)}`}>
                    {invoice.status}
                  </span>
                </td>
                <td>
                  {hasPermission('invoice', 'update') && invoice.status === 'Draft' && (
                    <button 
                      className="btn btn-sm btn-outline-warning me-2"
                      onClick={() => handleStatusUpdate(invoice._id, 'Sent')}
                    >
                      Send
                    </button>
                  )}
                  {hasPermission('invoice', 'update') && invoice.status === 'Sent' && (
                    <button 
                      className="btn btn-sm btn-outline-success me-2"
                      onClick={() => handleStatusUpdate(invoice._id, 'Paid')}
                    >
                      Mark Paid
                    </button>
                  )}
                  {hasPermission('invoice', 'delete') && (
                    <button 
                      className="btn btn-sm btn-outline-danger"
                      onClick={() => handleDelete(invoice._id)}
                    >
                      <i className="fas fa-trash"></i>
                    </button>
                  )}
                </td>
              </tr>
            )) : null}
          </tbody>
          </table>
        </div>
      )}

      {/* Pagination */}
      <div className="d-flex justify-content-between align-items-center mt-4">
        <div className="text-muted">
          Showing {((pagination.current - 1) * pagination.limit) + 1} to {Math.min(pagination.current * pagination.limit, pagination.total)} of {pagination.total} entries
        </div>
        <nav>
          <ul className="pagination mb-0">
            <li className={`page-item ${pagination.current === 1 ? 'disabled' : ''}`}>
              <button className="page-link" onClick={() => setPagination({...pagination, current: pagination.current - 1})} disabled={pagination.current === 1}>
                Previous
              </button>
            </li>
            {[...Array(pagination.pages)].map((_, i) => (
              <li key={i + 1} className={`page-item ${pagination.current === i + 1 ? 'active' : ''}`}>
                <button className="page-link" onClick={() => setPagination({...pagination, current: i + 1})}>
                  {i + 1}
                </button>
              </li>
            ))}
            <li className={`page-item ${pagination.current === pagination.pages ? 'disabled' : ''}`}>
              <button className="page-link" onClick={() => setPagination({...pagination, current: pagination.current + 1})} disabled={pagination.current === pagination.pages}>
                Next
              </button>
            </li>
          </ul>
        </nav>
      </div>

      {/* Create Invoice Modal */}
      {showModal && (
        <div className="modal show d-block" tabIndex="-1">
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Create Invoice</h5>
                <button type="button" className="btn-close" onClick={() => setShowModal(false)}></button>
              </div>
              <form onSubmit={handleSubmit}>
                <div className="modal-body">
                  <div className="mb-3">
                    <label className="form-label">Employee</label>
                    <Select
                      options={employeeOptions}
                      value={employeeOptions.find(e => e.value === formData.employeeId)}
                      onChange={(option) => setFormData({...formData, employeeId: option?.value || ''})}
                      placeholder="Select Employee"
                      required
                    />
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Month</label>
                    <input
                      type="month"
                      className="form-control"
                      value={formData.month}
                      onChange={(e) => setFormData({...formData, month: e.target.value})}
                      required
                    />
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Invoice Number</label>
                    <input
                      type="text"
                      className="form-control"
                      value={formData.invoiceNumber}
                      onChange={(e) => setFormData({...formData, invoiceNumber: e.target.value})}
                      placeholder="Auto-generated if empty"
                    />
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Timesheet Hours</label>
                    <input
                      type="number"
                      step="0.5"
                      min="0.5"
                      className="form-control"
                      value={formData.timesheetHours}
                      onChange={(e) => setFormData({...formData, timesheetHours: e.target.value})}
                      required
                    />
                  </div>
                  
                  {calculation && (
                    <div className="card bg-light">
                      <div className="card-body">
                        <h6>Invoice Preview
                          <InfoTooltip 
                            title="Calculation Preview"
                            content="This shows how the invoice amount is calculated based on the employee's current payroll condition, total hours worked, and margin tiers."
                          />
                        </h6>
                        <div className="row">
                          <div className="col-md-6">
                            <p><strong>Previous Hours:</strong> {calculation.previousTotalHours}</p>
                            <p><strong>New Total Hours:</strong> {calculation.newTotalHours}</p>
                            <p><strong>Invoice Amount:</strong> ${calculation.invoiceAmount.toFixed(2)}</p>
                          </div>
                          <div className="col-md-6">
                            <p><strong>Caliber Revenue:</strong> ${calculation.caliberRevenue.toFixed(2)}</p>
                            <p><strong>Consultant Pay:</strong> ${calculation.consultantPay.toFixed(2)}</p>
                            <p><strong>Bonus:</strong> ${calculation.bonus.toFixed(2)}</p>
                          </div>
                        </div>
                        {calculation.calculationBreakdown && calculation.calculationBreakdown.length > 0 && (
                          <div className="mt-3">
                            <h6>Calculation Breakdown:</h6>
                            {calculation.calculationBreakdown.map((tier, index) => (
                              <div key={index} className="small text-muted">
                                {tier.applicableHours} hours at {tier.rate} = ${tier.amount.toFixed(2)}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>
                    Cancel
                  </button>
                  <button type="submit" className="btn btn-primary" disabled={!calculation || isSubmitting}>
                    {isSubmitting ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                        Creating...
                      </>
                    ) : (
                      'Create Invoice'
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="modal show d-block" tabIndex="-1">
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Confirm Delete</h5>
                <button type="button" className="btn-close" onClick={() => setShowDeleteModal(false)}></button>
              </div>
              <div className="modal-body">
                <div className="text-center">
                  <i className="fas fa-exclamation-triangle text-warning" style={{fontSize: '3rem'}}></i>
                  <h5 className="mt-3">Are you sure?</h5>
                  <p className="text-muted">
                    You are about to delete {selectedInvoices.length} invoice(s). This action cannot be undone.
                  </p>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowDeleteModal(false)}>
                  Cancel
                </button>
                <button type="button" className="btn btn-danger" onClick={handleBulkDelete}>
                  <i className="fas fa-trash me-2"></i>Delete {selectedInvoices.length} Invoice(s)
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EnhancedInvoiceManagement;